import { useEffect, useState } from "react";
import { api, brl } from "@/lib/api";
import { useHideValues } from "@/components/HideValues";

export function useProductCosts() {
  const [map, setMap] = useState({});
  useEffect(() => { api.get("/products").then(({ data }) => setMap(Object.fromEntries((data.items || []).map((p) => [p.id, Number(p.cost) || 0]))))
    .catch(() => setMap({})); }, []);
  return map;
}

export function profitOf(it, costMap) {
  if (it.customer_supplied) return { unitCost: 0, cost: 0, revenue: 0, profit: 0, pct: null };
  const qty = Number(it.quantity) || 0, price = Number(it.unit_price) || 0, disc = Number(it.discount) || 0;
  const has = it.cost !== "" && it.cost != null;
  let unitCost = null, missing = false;
  if (has) unitCost = Number(it.cost) || 0;
  else if (it.product_id) unitCost = costMap[it.product_id] != null ? Number(costMap[it.product_id]) : null;
  else { unitCost = 0; missing = true; }
  if (unitCost == null) { return null; }
  const revenue = qty * price - disc;
  const cost = qty * unitCost;
  const profit = revenue - cost;
  const pct = revenue > 0 ? (profit / revenue) * 100 : null;
  return { unitCost, cost, revenue, profit, pct, missing };
}

export function ItemProfitLine({ item, costMap, testid }) {
  const { hide } = useHideValues();
  const p = profitOf(item, costMap);
  if (!p) {
    return <div data-testid={testid} className="rounded-md border border-dashed border-zinc-400 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950/40 px-2 py-1 text-[11px] text-zinc-700 dark:text-zinc-300">Lucro: peça não vinculada ao estoque — vincule para ver.</div>;
  }
  const color = p.pct == null ? "text-zinc-700 dark:text-zinc-300" : p.pct >= 25 ? "text-green-700 dark:text-green-300" : p.pct >= 10 ? "text-amber-700 dark:text-amber-300" : "text-red-700 dark:text-red-300";
  const fmt = (v) => (hide ? "•••••" : brl(v));
  return (
    <div data-testid={testid} className="rounded-md border border-amber-500/70 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-950/15 px-2 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
      <span className="font-mono-caps text-amber-800 dark:text-amber-200/80 shrink-0">Lucro</span>
      {!item.product_id && <span className={p.missing ? "text-red-700 dark:text-red-300 font-bold" : "text-zinc-700 dark:text-zinc-300"}>{p.missing ? "sem custo informado" : "custo informado"}</span>}
      <span data-testid={testid ? `${testid}-cost` : undefined} className="text-zinc-700 dark:text-zinc-300">Custo <b className="text-zinc-900 dark:text-zinc-100 tabular-nums">{fmt(p.cost)}</b></span>
      <span className="text-zinc-500 dark:text-zinc-600">·</span>
      <span data-testid={testid ? `${testid}-profit` : undefined} className="text-zinc-700 dark:text-zinc-300">Lucro <b className={`tabular-nums text-base ${color}`}>{fmt(p.profit)}</b> <span className={color}>({p.pct == null ? "—" : `${p.pct.toFixed(1)}%`})</span></span>
      <span className="ml-auto text-zinc-500 dark:text-zinc-600">só o dono vê</span>
    </div>
  );
}

export function BudgetProfitTotals({ items, costMap, testid }) {
  const { hide } = useHideValues();
  const rows = items.map((it) => profitOf(it, costMap));
  if (!rows.some(Boolean)) { return null; }
  const totalCost = rows.reduce((s, r) => s + (r ? r.cost : 0), 0);
  const totalRevKnown = rows.reduce((s, r) => s + (r ? r.revenue : 0), 0);
  const profit = totalRevKnown - totalCost;
  const pct = totalRevKnown > 0 ? (profit / totalRevKnown) * 100 : null;
  const fmt = (v) => (hide ? "•••••" : brl(v));
  const anyService = rows.some((r) => !r || r.missing);
  const nMissing = rows.filter((r) => !r || r.missing).length;
  return (
    <div data-testid={testid} className="mt-2 rounded-xl border border-amber-500/70 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-950/15 p-3 text-xs space-y-0.5">
      <div className="flex items-center justify-between"><span className="font-mono-caps text-amber-800 dark:text-amber-200/80">Lucro do orçamento <span className="text-zinc-600 dark:text-zinc-500 normal-case">(só o dono vê)</span></span>
        <span className="font-display text-lg font-black text-amber-700 dark:text-amber-brand tabular-nums">{fmt(profit)} <span className="text-xs font-sans text-zinc-700 dark:text-zinc-400">({pct == null ? "—" : `${pct.toFixed(1)}%`})</span></span></div>
      <div className="flex justify-between text-zinc-700 dark:text-zinc-300"><span>Custo total</span><span className="tabular-nums">{fmt(totalCost)}</span></div>
      {anyService && <div data-testid={`${testid}-missing`} className="text-amber-700 dark:text-amber-300 font-semibold">{nMissing} {nMissing === 1 ? "item" : "itens"} sem custo — lucro pode estar errado.</div>
      }
    </div>
  );
}
